import {ICON_FILES} from './iconFiles.generated.js';

/** @typedef {'body-xs'|'body-sm'|'body-md'|'body-lg'|'title-sm'|'title-lg'|'heading-sm'|'heading-md'|'heading-lg'|'heading-xl'|'heading-2xl'|'full'} IconSize */

/** Same table as App\View\Components\Ui\IconSize. */
const SIZE_CLASSES = {
    'body-xs': 'fg-body-xs',
    'body-sm': 'fg-body-sm',
    'body-md': 'fg-body-md',
    'body-lg': 'fg-body-lg',
    'title-sm': 'fg-title-sm',
    'title-lg': 'fg-title-lg',
    'heading-sm': 'fg-heading-sm',
    'heading-md': 'fg-heading-md',
    'heading-lg': 'fg-heading-lg',
    'heading-xl': 'fg-heading-xl',
    'heading-2xl': 'fg-heading-2xl',
    full: 'svg-icon--full',
};

const ARIA_ATTRIBUTES = ['aria-hidden', 'aria-describedby', 'aria-label'];

/**
 * The sprite markup of `<x-ui.icon>`, for icons that JS inserts. Same props as the Blade component.
 * @param {string} name Catalog name: the symbol id in the sprite without `icon-`
 * @param {object} [options]
 * @param {boolean} [options.filled] Use the `<name>-filled` drawing
 * @param {boolean} [options.bold] Draw the strokes at the Bold weight
 * @param {boolean} [options.paired] The box is one line of the text around it tall; false gives a square box
 * @param {IconSize|null} [options.size] Inherit the text size (null), a text style, or full to fill a box the container sizes
 * @param {Record<string, string>} [options.attributes] Extra attributes of the host `<svg>`; a `class` joins the icon classes
 * @returns {string}
 */
export function getSvgIconTemplate(name, {filled = false, bold = false, paired = true, size = null, attributes = {}} = {}) {
    if (!ICON_FILES.has(name)) {
        throw new Error(`Unknown icon [${name}]: resources/icons/${name}.svg does not exist.`);
    }
    if (filled && !ICON_FILES.has(`${name}-filled`)) {
        throw new Error(`Icon [${name}] has no filled style: resources/icons/${name}-filled.svg does not exist.`);
    }
    const {class: extraClass, ...otherAttributes} = attributes;
    const classes = ['svg-icon'];
    if (!paired) {
        classes.push('svg-icon--square');
    }
    if (bold) {
        classes.push('svg-icon--bold');
    }
    if (size !== null) {
        if (!Object.hasOwn(SIZE_CLASSES, size)) {
            throw new Error(`Unknown icon size [${size}]. Use null to follow the text, or one of: ${Object.keys(SIZE_CLASSES).join(', ')}.`);
        }
        classes.push(SIZE_CLASSES[size]);
    }
    if (extraClass) {
        classes.push(extraClass);
    }
    if (!ARIA_ATTRIBUTES.some((attribute) => attribute in otherAttributes)) {
        otherAttributes['aria-hidden'] = 'true';
    }
    const attributeString = Object.entries(otherAttributes).map(([key, value]) => ` ${key}="${value}"`).join('');
    const file = filled ? `${name}-filled` : name;

    return `<svg xmlns="http://www.w3.org/2000/svg" data-icon-name="${name}"${attributeString} class="${classes.join(' ')}"><use href="#icon-${file}"></use></svg>`;
}
